// The draft replay (scenario C11, PLAN-2026-10-31-the-draft-replay): a
// finished draft played back pick by pick, the morning after, as though it
// were live. The draft ran in an instant with nobody watching; this is how
// you watch it anyway, over coffee.
//
// THE PACE IS OURS, the moments are the server's. A pick is on the clock for
// a beat and then in, longer for mine and for one the server says is worth
// lingering on (`DraftReplayMoment`); the wire carries the facts and no
// milliseconds, so this table is a presentation timeline over the record
// (PLAN §4, B-583).
//
// IT SAYS IT IS A REPLAY. The header says when the draft ran, and the stage
// opens on it, paused: every team is already set, and nobody has to sit
// through it to see their own (B-582).
//
// Drawn through the kit (LOOKS.md Part 7).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatInZone,
  type DraftReplayMoment,
  type DraftReplayPick,
  type DraftReplayView,
  type TeamRef,
} from '@fsp/domain';
import { useLeagueBase, useDraftReplay, useReaderZone } from '../data/hooks';
import { useAtLeast } from '../data/detail';
import { EmptyState, PositionPill, ProTeamCode, TeamMark } from '../kit';
import { Panel } from '../kit/Panel';
import {
  Button,
  Caption,
  IconButton,
  Line,
  ListRow,
  OneOf,
  RowName,
  RowSub,
  Scrubber,
  Stack,
  Text,
  Title,
} from '../kit/ui';
import { useTier } from '../shell/useTier';
import { FromLeague } from './shared/FromLeague';
import { PlayerName, TeamName } from './shared/Names';
import { DraftBoardGrid, type BoardSquare } from './LeagueDraftScreen';
import '../styles/screens/league-draft.css';
import '../styles/screens/draft-replay.css';

export function DraftReplayScreen() {
  return (
    <FromLeague snapshot={useDraftReplay()}>{(view) => <ReplayBody view={view} />}</FromLeague>
  );
}

type Speed = '1' | '2' | '4';
const SPEEDS: readonly { value: Speed; label: string }[] = [
  { value: '1', label: '1×' },
  { value: '2', label: '2×' },
  { value: '4', label: '4×' },
];

/**
 * How long a pick is on the clock, and then in, at 1× (PLAN §4, B-583). A
 * routine pick a beat; a keeper, chosen last season, a quick one; mine and
 * one with a moment long enough to read; the name my list had next, gone
 * one pick before mine, longest of all.
 */
function beat(pick: DraftReplayPick, mine: boolean): { clock: number; in: number } {
  if (pick.by === 'keeper') return { clock: 200, in: 500 };
  if (pick.moments.some((m) => m.kind === 'listed' && m.missedByOne))
    return { clock: 500, in: 4000 };
  if (mine) return { clock: 900, in: 2300 + (pick.moments.length > 0 ? 1400 : 0) };
  if (pick.moments.length > 0) return { clock: 500, in: 2500 };
  return { clock: 500, in: 1100 };
}

/** Where the replay is: how many picks are in, and whether the next is on the clock. */
interface At {
  shown: number;
  clock: boolean;
}

const reducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

function ReplayBody({ view }: { view: DraftReplayView }) {
  const navigate = useNavigate();
  const base = useLeagueBase();
  const zone = useReaderZone();
  const tier = useTier();
  const standard = useAtLeast('standard');
  const picks = view.picks;
  const total = picks.length;
  const mineId = view.myTeamId;
  const teamOf = useMemo(() => new Map(view.seats.map((s) => [s.team.id, s.team])), [view.seats]);

  const [at, setAt] = useState<At>({ shown: 0, clock: false });
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<Speed>('1');
  const done = at.shown >= total && !at.clock;

  // THE CLOCK THAT PLAYS IT: one timer for the beat in front of it.
  useEffect(() => {
    if (!playing) return;
    if (done) {
      setPlaying(false);
      return;
    }
    // On the clock: the next pick's own beat. In: the pick just made, held
    // for its own, and before the first, none.
    const next = picks[at.shown];
    const last = at.shown > 0 ? picks[at.shown - 1] : undefined;
    const wait = at.clock
      ? next
        ? beat(next, next.teamId === mineId).clock
        : 0
      : last
        ? beat(last, last.teamId === mineId).in
        : 0;
    const t = window.setTimeout(
      () =>
        setAt(at.clock ? { shown: at.shown + 1, clock: false } : { shown: at.shown, clock: true }),
      wait / Number(speed),
    );
    return () => window.clearTimeout(t);
  }, [playing, at, done, picks, speed, mineId]);

  const go = (shown: number) => setAt({ shown: Math.max(0, Math.min(total, shown)), clock: false });
  // BACK FROM A PICK ON THE CLOCK is the pick just in, not the one before it.
  const back = () => go(at.clock ? at.shown : at.shown - 1);
  const play = () => {
    if (done) go(0);
    setPlaying(true);
  };
  const toMine = () => {
    const next = picks.findIndex((p, i) => i >= at.shown && p.teamId === mineId);
    if (next >= 0) go(next + 1);
  };
  const mineAhead = mineId !== null && picks.some((p, i) => i >= at.shown && p.teamId === mineId);

  // A KEYBOARD PLAYS IT TOO: space to play and pause, the arrows to step, as
  // a video does. Not while a field or a button has it, which take their own.
  const keys = useRef({ playing, done, go, back, play, at });
  keys.current = { playing, done, go, back, play, at };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, button, [role="slider"], [contenteditable]'))
        return;
      const k = keys.current;
      if (e.key === ' ') {
        e.preventDefault();
        if (k.playing) setPlaying(false);
        else k.play();
      } else if (e.key === 'ArrowRight') {
        k.go(k.at.shown + 1);
      } else if (e.key === 'ArrowLeft') {
        k.back();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // THE BOARD FOLLOWS THE STAGE: its square kept in view, across inside the
  // board's own frame and, where the board has a region of its own, down it.
  const onClock = at.clock ? picks[at.shown] : undefined;
  const latest = at.shown > 0 ? picks[at.shown - 1] : undefined;
  const ringed = onClock?.overall ?? latest?.overall ?? null;
  const boardRef = useRef<HTMLDivElement | null>(null);
  const regionRef = useRef<HTMLElement | null>(null);
  const wide = tier !== 'phone';
  useEffect(() => {
    const frame = boardRef.current?.querySelector<HTMLElement>('.fsp-league-draft-board');
    const cell = frame?.querySelector<HTMLElement>('[data-current]');
    if (!frame || !cell) return;
    const smooth = reducedMotion() ? 'auto' : 'smooth';
    // FROM THE FRAME, not `offsetLeft`, whose measure is the nearest positioned
    // box: the content area, which put the square a column's width off on a
    // tablet, where the stage's column sits to the left of the board.
    const left =
      cell.getBoundingClientRect().left - frame.getBoundingClientRect().left + frame.scrollLeft;
    frame.scrollTo({
      left: left - frame.clientWidth / 2 + cell.offsetWidth / 2,
      behavior: smooth,
    });
    const region = regionRef.current;
    if (wide && region) {
      const top = cell.getBoundingClientRect().top - region.getBoundingClientRect().top;
      if (top < 0 || top > region.clientHeight - cell.offsetHeight) {
        region.scrollBy({ top: top - region.clientHeight / 3, behavior: smooth });
      }
    }
  }, [ringed, wide]);

  if (!view.available) {
    return (
      <div className="fsp-screen">
        <div className="fsp-pinned">
          <Title>Draft replay</Title>
        </div>
        <EmptyState>The replay plays the draft back once it has run.</EmptyState>
      </div>
    );
  }

  const ranAt = view.startedAt ?? view.completedAt;
  const when =
    ranAt === null || zone === null
      ? null
      : formatInZone(ranAt, zone, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        });
  const auction = view.type === 'auction';
  const squares: BoardSquare[] = boardSquares(view, at.shown);
  const myPicks = picks.slice(0, at.shown).filter((p) => p.teamId === mineId);
  const mySlots = mineId === null ? 0 : picks.filter((p) => p.teamId === mineId).length;
  const marks = picks.flatMap((p, i) => (p.teamId === mineId ? [i + 1] : []));
  const heard = latest ? spoken(latest, teamOf, mineId) : '';

  const stage = (
    <Stage
      view={view}
      onClock={onClock ?? null}
      latest={latest ?? null}
      shown={at.shown}
      when={when}
      teamOf={teamOf}
      standard={standard}
      done={done}
      onRecap={() => navigate(`${base}/draft-recap`)}
    />
  );
  const controls = (
    <div className="fsp-replay-controls">
      <Line gap={2} wrap>
        <IconButton
          icon="previous"
          variant="secondary"
          label="Back one pick"
          disabled={at.shown === 0 && !at.clock}
          onClick={back}
        />
        {playing ? (
          <Button icon="pause" onClick={() => setPlaying(false)}>
            Pause
          </Button>
        ) : (
          <Button variant="primary" icon={done ? 'refresh' : 'play'} onClick={play}>
            {done ? 'Watch again' : 'Play'}
          </Button>
        )}
        <IconButton
          icon="next"
          variant="secondary"
          label="Forward one pick"
          disabled={done}
          onClick={() => go(at.shown + 1)}
        />
        <div className="fsp-replay-speed">
          <OneOf label="Speed" hideLabel options={SPEEDS} value={speed} onChange={setSpeed} />
        </div>
      </Line>
      <Scrubber
        label="Where in the draft"
        value={at.shown}
        max={total}
        marks={marks}
        onChange={(v) => go(v)}
        said={(v) => (v === 0 ? 'Before the first pick' : `Pick ${String(v)} of ${String(total)}`)}
      />
      <Line gap={2} wrap>
        {mineId !== null ? (
          <Button size="xs" variant="quiet" disabled={!mineAhead} onClick={toMine}>
            My next pick
          </Button>
        ) : null}
        <Button size="xs" variant="quiet" icon="to-end" disabled={done} onClick={() => go(total)}>
          To the end
        </Button>
      </Line>
    </div>
  );
  const myTeam =
    mineId === null ? null : (
      <Panel
        title="Your team"
        meta={standard ? `${String(myPicks.length)} of ${String(mySlots)}` : undefined}
      >
        {myPicks.length === 0 ? (
          <Text tone="secondary" size="sm" block>
            Your picks join here as they are made.
          </Text>
        ) : (
          <Stack gap={0}>
            {myPicks.map((p) => (
              <ListRow
                key={p.overall}
                lead={<PositionPill pos={p.player.positions[0] ?? '—'} size="sm" />}
              >
                <RowName>
                  <PlayerName player={p.player} />
                </RowName>
                <RowSub>
                  {auction
                    ? `$${String(p.price ?? 0)}`
                    : `Round ${String(p.round)}${standard ? ` · pick ${String(p.overall)}` : ''}`}
                </RowSub>
              </ListRow>
            ))}
          </Stack>
        )}
      </Panel>
    );
  const justNow = (
    <Panel title="Just now">
      {at.shown === 0 ? (
        <Text tone="secondary" size="sm" block>
          The picks so far, newest first.
        </Text>
      ) : (
        <Stack gap={0}>
          {picks
            .slice(Math.max(0, at.shown - 5), at.shown)
            .reverse()
            .map((p) => {
              const team = teamOf.get(p.teamId);
              return (
                <ListRow
                  key={p.overall}
                  selected={p.teamId === mineId}
                  lead={team ? <TeamMark team={team} size={22} /> : null}
                >
                  <RowName>
                    <PlayerName player={p.player} />
                  </RowName>
                  <RowSub wrap>
                    {[team?.name ?? '', ...p.moments.map((m) => m.line)]
                      .filter(Boolean)
                      .join(' · ')}
                  </RowSub>
                </ListRow>
              );
            })}
        </Stack>
      )}
    </Panel>
  );
  const board = (
    <Stack gap={2}>
      <Caption>The board</Caption>
      <div ref={boardRef} className="fsp-replay-board">
        <DraftBoardGrid
          seats={view.seats}
          rounds={view.rounds}
          auction={auction}
          myTeamId={mineId}
          squares={squares}
          current={ringed}
          fresh={at.clock ? null : (latest?.overall ?? null)}
        />
      </div>
    </Stack>
  );

  return (
    <div className="fsp-screen">
      <div className="fsp-pinned">
        <Stack gap={0}>
          <Title>Draft replay</Title>
          <Text tone="secondary" size="xs">
            {when === null ? 'A replay of the draft' : `A replay of the draft that ran ${when}`}
          </Text>
        </Stack>
      </div>
      {/* Each pick in, said to a screen reader as the board fills. */}
      <div className="fsp-sr" aria-live="polite">
        {heard}
      </div>
      {wide ? (
        <div className="fsp-regions fsp-replay-regions" data-wide>
          <section className="fsp-region" aria-label="The pick">
            <div className="fsp-replay-column">
              {stage}
              {controls}
              {myTeam}
              {justNow}
            </div>
          </section>
          <section
            className="fsp-region"
            aria-label="The board"
            ref={(el) => {
              regionRef.current = el;
            }}
          >
            <div className="fsp-league-draft-body">{board}</div>
          </section>
        </div>
      ) : (
        <>
          <div className="fsp-pinned fsp-replay-phone-top">
            {stage}
            {controls}
          </div>
          <div className="fsp-regions fsp-replay-regions">
            <section className="fsp-region" aria-label="Your team and the board">
              <div className="fsp-league-draft-body">
                {myTeam}
                {board}
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
}

/** Every square of the board, those past `shown` still to be made. */
function boardSquares(view: DraftReplayView, shown: number): BoardSquare[] {
  return view.picks.map((p, i) => ({
    overall: p.overall,
    round: p.round,
    slot: p.slot,
    teamId: p.teamId,
    originalTeamId: p.originalTeamId,
    player: i < shown ? p.player : null,
    price: i < shown ? p.price : null,
  }));
}

/** A pick in, as a screen reader hears it. */
function spoken(
  pick: DraftReplayPick,
  teamOf: ReadonlyMap<string, TeamRef>,
  mineId: string | null,
): string {
  const who =
    pick.teamId === mineId ? 'You take' : `${teamOf.get(pick.teamId)?.name ?? 'A team'} takes`;
  return [`${who} ${pick.player.name}.`, ...pick.moments.map((m) => m.line)].join(' ');
}

function Stage({
  view,
  onClock,
  latest,
  shown,
  when,
  teamOf,
  standard,
  done,
  onRecap,
}: {
  view: DraftReplayView;
  onClock: DraftReplayPick | null;
  latest: DraftReplayPick | null;
  shown: number;
  when: string | null;
  teamOf: ReadonlyMap<string, TeamRef>;
  standard: boolean;
  done: boolean;
  onRecap: () => void;
}) {
  const auction = view.type === 'auction';
  const mineId = view.myTeamId;

  // BEFORE THE FIRST PICK: when it ran, that every team is set, and Play.
  if (shown === 0 && onClock === null) {
    return (
      <div className="fsp-replay-stage" data-state="start">
        <Text weight="bold" block>
          {when === null ? 'Your draft is done.' : `Your draft ran on ${when}.`}
        </Text>
        <Text tone="secondary" size="sm" block>
          Every team is already set. This plays it back pick by pick, as though it were live.
        </Text>
      </div>
    );
  }

  const pick = onClock ?? latest!;
  const team = teamOf.get(pick.teamId);
  const mine = pick.teamId === mineId;
  // AN AUCTION HAS NOBODY ON THE CLOCK: the next player is up, and whose he
  // is shows when he is sold.
  const place = auction
    ? `Sale ${String(pick.overall)}`
    : `Round ${String(pick.round)} · Pick ${String(pick.slot)}${standard ? ` · ${String(pick.overall)} overall` : ''}`;
  if (auction && onClock) {
    return (
      <div className="fsp-replay-stage" data-state="clock" key={`${String(pick.overall)}:clock`}>
        <Text tone="secondary" size="xs" numeric>
          {place}
        </Text>
        <Text tone="secondary" size="sm" block>
          <span className="fsp-replay-onclock">The next player is up</span>
        </Text>
      </div>
    );
  }

  return (
    <div
      className="fsp-replay-stage"
      data-state={onClock ? 'clock' : 'in'}
      data-mine={mine || undefined}
      // A new pick is a new stage, so its arrival plays once.
      key={`${String(pick.overall)}:${onClock ? 'clock' : 'in'}`}
    >
      <Line spread gap={2}>
        <Text tone="secondary" size="xs" numeric>
          {place}
        </Text>
        {mine ? (
          <Text tone="accent" size="xs" weight="bold">
            Your pick
          </Text>
        ) : null}
      </Line>
      <Line gap={2}>
        {team ? <TeamMark team={team} size={36} /> : null}
        <Stack gap={0}>
          <Text weight="bold" block>
            {team ? <TeamName team={team} /> : 'A team'}
          </Text>
          <Text tone="secondary" size="xs" block>
            {onClock ? (
              <span className="fsp-replay-onclock">On the clock</span>
            ) : auction ? (
              `Bought for $${String(pick.price ?? 0)}`
            ) : mine ? (
              'You take'
            ) : (
              'Takes'
            )}
          </Text>
        </Stack>
      </Line>
      {onClock ? null : (
        <div className="fsp-replay-player">
          <PositionPill pos={pick.player.positions[0] ?? '—'} />
          <span className="fsp-replay-player-name">
            <PlayerName player={pick.player} />
          </span>
          <ProTeamCode code={pick.player.proTeam} />
        </div>
      )}
      {onClock ? null : pick.moments.length > 0 ? (
        <Stack gap={1}>
          {pick.moments.map((m, i) => (
            <Moment key={i} moment={m} standard={standard} />
          ))}
        </Stack>
      ) : null}
      {done ? (
        <div className="fsp-replay-end">
          <Text weight="bold" block>
            That’s the draft.
          </Text>
          {/* Watch again is the controls' own Play, under it. */}
          <div>
            <Button onClick={onRecap}>Draft recap</Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** One moment's line: the room's in the accent, my list's in the warm one. */
function Moment({ moment, standard }: { moment: DraftReplayMoment; standard: boolean }) {
  return (
    <div className="fsp-replay-moment" data-kind={moment.kind}>
      <span>{moment.line}</span>
      {/* A run's length is a figure, from Standard (DETAIL_LEVELS rule 1). */}
      {moment.kind === 'run' && standard ? (
        <span className="fsp-replay-count">×{moment.count}</span>
      ) : null}
    </div>
  );
}
